import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery } from "@rocicorp/zero/react";
import { appQueries } from "@production-hub/shared";

export interface PersonBookedProject {
  id: string;
  project_id: string;
  status: string;
  project: {
    id: string;
    name: string;
    number: string | null;
    event_start_date: string | null;
    event_end_date: string | null;
    start_date: string | null;
    end_date: string | null;
    status: string | null;
  };
}

export interface PersonFunctionAssignment {
  id: string;
  function_id: string;
  remark: string | null;
  project_function: {
    id: string;
    project_id: string;
    function_date: string;
    name: string;
    start_time: string | null;
    end_time: string | null;
    project: {
      id: string;
      name: string;
      number: string | null;
      event_start_date: string | null;
      event_end_date: string | null;
      start_date: string | null;
      end_date: string | null;
      status: string | null;
    };
  };
}

type ZeroProjectRow = {
  id: string;
  name: string;
  number?: string | null;
  eventStartDate?: number | string | null;
  eventEndDate?: number | string | null;
  startDate?: number | string | null;
  endDate?: number | string | null;
  status?: string | null;
};

type ZeroAvailabilityBookingRow = {
  id: string;
  projectID: string;
  personID: string;
  status: string;
  availableDays?: (number | string)[] | null;
  project?: ZeroProjectRow | null;
};

type ZeroFunctionAssignmentRow = {
  id: string;
  functionID: string;
  personID: string;
  remark?: string | null;
  projectFunction?: {
    id: string;
    projectID: string;
    functionDate: number | string;
    name: string;
    startTime?: number | string | null;
    endTime?: number | string | null;
    project?: ZeroProjectRow | null;
  } | null;
};

type ZeroFunctionSeriesProjectRow = {
  projectID: string;
};

function toDateString(value: number | string | null | undefined) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value ?? null;
}

function toTimeString(value: number | string | null | undefined) {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;

  const totalSeconds = Math.floor(value / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0",
  )}:${String(seconds).padStart(2, "0")}`;
}

function toBookedProject(
  row: ZeroAvailabilityBookingRow,
): PersonBookedProject | null {
  if (!row.project) return null;
  return {
    id: row.id,
    project_id: row.projectID,
    status: row.status,
    project: {
      id: row.project.id,
      name: row.project.name,
      number: row.project.number ?? null,
      event_start_date: toDateString(row.project.eventStartDate),
      event_end_date: toDateString(row.project.eventEndDate),
      start_date: toDateString(row.project.startDate),
      end_date: toDateString(row.project.endDate),
      status: row.project.status ?? null,
    },
  };
}

function toEmployeeBookedAvailability(
  row: ZeroAvailabilityBookingRow,
): EmployeeBookedAvailability | null {
  if (!row.project) return null;
  return {
    id: row.id,
    person_id: row.personID,
    project_id: row.projectID,
    available_days: (row.availableDays ?? [])
      .map((day) => toDateString(day))
      .filter((day): day is string => !!day),
    project: {
      id: row.project.id,
      name: row.project.name,
      number: row.project.number ?? null,
    },
  };
}

function toPersonFunctionAssignment(
  row: ZeroFunctionAssignmentRow,
): PersonFunctionAssignment | null {
  if (!row.projectFunction?.project) return null;
  return {
    id: row.id,
    function_id: row.functionID,
    remark: row.remark ?? null,
    project_function: {
      id: row.projectFunction.id,
      project_id: row.projectFunction.projectID,
      function_date: toDateString(row.projectFunction.functionDate) ?? "",
      name: row.projectFunction.name,
      start_time: toTimeString(row.projectFunction.startTime),
      end_time: toTimeString(row.projectFunction.endTime),
      project: {
        id: row.projectFunction.project.id,
        name: row.projectFunction.project.name,
        number: row.projectFunction.project.number ?? null,
        event_start_date: toDateString(
          row.projectFunction.project.eventStartDate,
        ),
        event_end_date: toDateString(row.projectFunction.project.eventEndDate),
        start_date: toDateString(row.projectFunction.project.startDate),
        end_date: toDateString(row.projectFunction.project.endDate),
        status: row.projectFunction.project.status ?? null,
      },
    },
  };
}

function toEmployeeFunctionAssignment(
  row: ZeroFunctionAssignmentRow,
): EmployeeFunctionAssignment | null {
  if (!row.projectFunction?.project) return null;
  return {
    id: row.id,
    person_id: row.personID,
    project_function: {
      function_date: toDateString(row.projectFunction.functionDate) ?? "",
      project: {
        id: row.projectFunction.project.id,
        name: row.projectFunction.project.name,
        number: row.projectFunction.project.number ?? null,
      },
    },
  };
}

export function usePersonBookedProjects(personId: string | undefined) {
  const query = personId
    ? appQueries.projectAvailability.bookedByPerson({ personID: personId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!personId });
  const bookings = ((data ?? []) as ZeroAvailabilityBookingRow[])
    .map(toBookedProject)
    .filter((booking): booking is PersonBookedProject => !!booking);

  return {
    data: bookings,
    isLoading:
      !!personId && details.type !== "complete" && bookings.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export interface EmployeeFunctionAssignment {
  id: string;
  person_id: string;
  project_function: {
    function_date: string;
    project: {
      id: string;
      name: string;
      number: string | null;
    };
  };
}

export function useEmployeeFunctionAssignments(
  employeeIds: string[] | undefined,
) {
  const query = employeeIds?.length
    ? appQueries.functionAssignments.byPeople({ personIDs: employeeIds })
    : undefined;
  const [data, details] = useZeroQuery(query, {
    enabled: !!employeeIds?.length,
  });
  const assignments = ((data ?? []) as ZeroFunctionAssignmentRow[])
    .map(toEmployeeFunctionAssignment)
    .filter(
      (assignment): assignment is EmployeeFunctionAssignment => !!assignment,
    );

  return {
    data: assignments,
    isLoading:
      !!employeeIds?.length &&
      details.type !== "complete" &&
      assignments.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export interface EmployeeBookedAvailability {
  id: string;
  person_id: string;
  project_id: string;
  available_days: string[];
  project: {
    id: string;
    name: string;
    number: string | null;
  };
}

export function useEmployeeBookedAvailability(
  employeeIds: string[] | undefined,
) {
  const query = employeeIds?.length
    ? appQueries.projectAvailability.bookedByPeople({ personIDs: employeeIds })
    : undefined;
  const [data, details] = useZeroQuery(query, {
    enabled: !!employeeIds?.length,
  });
  const availability = ((data ?? []) as ZeroAvailabilityBookingRow[])
    .map(toEmployeeBookedAvailability)
    .filter((booking): booking is EmployeeBookedAvailability => !!booking);

  return {
    data: availability,
    isLoading:
      !!employeeIds?.length &&
      details.type !== "complete" &&
      availability.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useProjectIdsWithFunctions() {
  const [data, details] = useZeroQuery(
    appQueries.projectFunctionSeries.listProjectIDs(),
  );
  const projectIds = [
    ...new Set(
      ((data ?? []) as ZeroFunctionSeriesProjectRow[]).map(
        (row) => row.projectID,
      ),
    ),
  ];

  return {
    data: projectIds,
    isLoading: details.type !== "complete" && projectIds.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function usePersonFunctionAssignments(personId: string | undefined) {
  const query = personId
    ? appQueries.functionAssignments.byPerson({ personID: personId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!personId });
  const assignments = ((data ?? []) as ZeroFunctionAssignmentRow[])
    .map(toPersonFunctionAssignment)
    .filter(
      (assignment): assignment is PersonFunctionAssignment => !!assignment,
    );

  return {
    data: assignments,
    isLoading:
      !!personId && details.type !== "complete" && assignments.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}
